import Polling from '@app/utils/polling'

import type { QueryResultsMessage } from './types'

type Handler = (message: QueryResultsMessage) => void

// What MessageBus hands a subscriber: the payload, its global id, and its
// position in the channel's backlog.
type ChannelHandler = (
  message: QueryResultsMessage,
  globalId?: number,
  messageId?: number
) => void

type SeenMessage = { id: number; message: QueryResultsMessage }

type ChannelSubscription = {
  polling: Polling<ChannelHandler>
  handlers: Set<Handler>
  seen: SeenMessage[]
}

// Enough to cover the gap between a page capturing its backlog position and a
// second listener on that page joining, without holding a channel's history.
const REPLAY_BUFFER_SIZE = 100

const subscriptions = new Map<string, ChannelSubscription>()

function openChannel(channel: string, lastId: number): ChannelSubscription {
  const handlers = new Set<Handler>()
  const seen: SeenMessage[] = []

  const polling = new Polling<ChannelHandler>(
    channel,
    lastId,
    (message, _globalId, messageId) => {
      seen.push({ id: messageId ?? Number.MAX_SAFE_INTEGER, message })
      if (seen.length > REPLAY_BUFFER_SIZE) seen.shift()

      handlers.forEach((handler) => handler(message))
    }
  )
  polling.subscribe()

  return { polling, handlers, seen }
}

// The one place that decides a run is over, for every surface showing a
// spinner for it. The channel also carries chunk progress and per-chunk
// errors, and a bare error is not terminal — a failed chunk publishes one and
// the run carries on. Returns the ref of the run that ended, or null.
export function endedRunRef(message: QueryResultsMessage): string | null {
  if (!message?.segment_run_done && !message?.segment_run_discarded) return null

  return message.request_ref || null
}

// One MessageBus subscription per channel, shared by everything listening for
// run state on it. message-bus-client keeps a single poll cursor per channel
// rather than one per callback — the last subscribe wins, and it aborts the
// poll in flight — so a second subscription would drag the shared cursor past
// messages the first listener had not been handed yet.
//
// A listener joining later hands over the position it was rendered at and gets
// whatever the shared subscription has already collected past it, so it cannot
// miss the end of a run it is showing a spinner for.
//
// Returns the unsubscribe; the channel closes once its last listener leaves.
export function subscribeToRunState(
  channel: string,
  lastId: number,
  handler: Handler
): () => void {
  let subscription = subscriptions.get(channel)

  if (subscription) {
    subscription.seen
      .filter((seen) => seen.id > lastId)
      .forEach((seen) => handler(seen.message))
  } else {
    subscription = openChannel(channel, lastId)
    subscriptions.set(channel, subscription)
  }

  subscription.handlers.add(handler)

  return () => {
    const current = subscriptions.get(channel)
    if (!current) return

    current.handlers.delete(handler)
    if (current.handlers.size > 0) return

    current.polling.unsubscribe()
    subscriptions.delete(channel)
  }
}
